import PageHeader from "./Components/PageHeader/PageHeader";
import EntitySelector from "./Components/EntitySelector/EntitySelector";
import FiltersRow from "./Components/FiltersRow/FiltersRow";

import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { getSuggestSearchAuthors, getAuthorProfileSummaryByID } from "Explore/Services/Author";
import { getSuggestSearchInstitutions, getInstitutionDetailSummaryByID } from "Explore/Services/Institution";
import { getSuggestSearchUnits, getUnitSummaryById } from "Explore/Services/Unit";

import { FilePlus, Table, BarChart2 } from "lucide-react";
import AddToReportModal from "Explore/Components/AddToReportModal";

import { Button } from "components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";

import AnalysisTable from "./Components/AnalysisTable/AnalysisTable";
import VisualViewTest from "./Components/VisualViewTest/VisualViewTest";
import { ANALYSIS_SORT_MAP } from "Explore/Config/constraint";

const Analysis = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const urlEntityType = searchParams.get("entity_type") || location.state?.entityType || "institution";
  const urlYearRange = searchParams.get("year_range") || location.state?.filters?.yearRange || "last_10_years";
  const urlSubjectArea = searchParams.get("subject_area") ? searchParams.get("subject_area").split(",") : (location.state?.filters?.subjectArea || []);
  const urlState = searchParams.get("state") ? searchParams.get("state").split(",") : (location.state?.filters?.state || []);
  const urlKeywordsOperator = searchParams.get("keywords_operator") || location.state?.filters?.keywordsOperator || "or";

  const [entityType, setEntityType] = useState(urlEntityType);
  const [selectedEntities, setSelectedEntities] = useState(location.state?.selectedEntities || []);
  const [filters, setFilters] = useState(location.state?.filters || {
    yearRange: urlYearRange,
    subjectArea: urlSubjectArea,
    state: urlState,
    keywordsOperator: urlKeywordsOperator,
    geography: "us",
    metric: "publications",
    collaboratorType: urlEntityType === "researcher" ? "researcher" : "institution",
  });

  useEffect(() => {
    const urlEntities = searchParams.get("entity_ids");

    const clearUrlParams = () => {
      navigate(location.pathname, { replace: true, state: location.state });
    };

    if (urlEntities && selectedEntities.length === 0) {
      const entityIds = urlEntities.split(",").map(id => id.trim()).filter(Boolean);

      if (entityIds.length === 0) {
        clearUrlParams();
        return;
      }

      const fetchPromises = entityIds.map(id => {
        return new Promise((resolve) => {
          const handleResult = (data) => resolve({ ...data, id, type: urlEntityType });

          if (urlEntityType === "researcher") {
            getAuthorProfileSummaryByID(id, handleResult);
          } else if (urlEntityType === "institution") {
            getInstitutionDetailSummaryByID(id, handleResult);
          } else if (urlEntityType === "unit") {
            getUnitSummaryById(id).then(handleResult).catch(() => resolve(null));
          } else {
            resolve(null);
          }
        });
      });

      Promise.all(fetchPromises).then((results) => {
        const validEntities = results.filter(Boolean);
        if (validEntities.length > 0) {
          setSelectedEntities(validEntities);
        }
        clearUrlParams();
      });
    } else {
      if (Array.from(searchParams.keys()).length > 0) {
        clearUrlParams();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pinnedEntities, setPinnedEntities] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [rawTableRows, setRawTableRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [topN, setTopN] = useState(5);
  const [sortConfig, setSortConfig] = useState({ key: 'totalPubs', direction: 'desc' });
  const [pendingEntities, setPendingEntities] = useState({ hasChanges: false, entities: [] });

  const handlePendingChange = useCallback((hasChanges, entities) => {
    setPendingEntities(prev => {
      if (prev.hasChanges === hasChanges && prev.entities === entities) return prev;
      return { hasChanges, entities };
    });
  }, []);

  const [viewMode, setViewMode] = useState(searchParams.get("view_mode") || "table");
  const [isAddToReportModalOpen, setIsAddToReportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [searchOptions, setSearchOptions] = useState([]);
  const searchDebounceRef = useRef(null);

  /**
   * Normalize a raw suggest API result into an AntD AutoComplete option.
   * Author and Institution APIs only return { id, name }.
   * Unit API returns { id, name, level, institution_id, institution_name }.
   */
  const normalizeEntityForSearch = useCallback((raw, type) => {
    const entity = {
      id: raw.id,
      name: raw.name,
      type,
      ...(type === "unit" && {
        institution_id: raw.institution_id,
        institution_name: raw.institution_name,
        level: raw.level,
      }),
    };
    return {
      value: raw.name,
      label: raw.name,
      entity, // named 'entity' (not 'data') to avoid conflict with rc-select's FlattenOptionData wrapper
    };
  }, []);

  /**
   * Debounced search handler — called by EntitySelector's AutoComplete onSearch.
   * Picks the correct suggest API based on the current entityType.
   */
  const handleSearch = useCallback((query) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!query || query.length < 2) {
      setSearchOptions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      const callback = (results) => {
        if (!Array.isArray(results)) return;
        setSearchOptions(results.map((r) => normalizeEntityForSearch(r, entityType)));
      };
      if (entityType === "institution") {
        getSuggestSearchInstitutions(query, callback);
      } else if (entityType === "researcher") {
        getSuggestSearchAuthors(query, callback);
      } else if (entityType === "unit") {
        getSuggestSearchUnits(query, callback);
      }
    }, 300);
  }, [entityType, normalizeEntityForSearch]);

  const handleAddToReport = () => {
    setExportFormat("pdf");
    setIsAddToReportModalOpen(true);
  };
  const handleExportXLSX = () => {
    setExportFormat("xlsx");
    setIsAddToReportModalOpen(true);
  };

  const handleEntityClear = () => {
    setTableLoading(true);
    setSelectedEntities([]);
    setFilters({
      yearRange: "all",
      subjectArea: [],
      geography: "us",
      metric: "publications",
      collaboratorType: entityType === "researcher" ? "researcher" : "institution",
    });
  };

  const exportEntities = pinnedEntities.length > 0 ? pinnedEntities : tableRows.slice(0, topN);

  const handleSelectedEntitiesChange = (entities) => {
    setTableLoading(true);
    setSelectedEntities(entities);
    if (entities.length > 0) {
      const type = entities[0].type;
      if (type && type !== entityType) {
        setEntityType(type);
      }
      setFilters(prev => ({
        ...prev,
        collaboratorType: type === "researcher" ? "researcher" : "institution"
      }));
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex flex-col flex-1 p-6 gap-6">
        <PageHeader />

        <EntitySelector
          entityType={entityType}
          onEntityTypeChange={(type) => {
            setTableLoading(true);
            setEntityType(type);
            setSearchOptions([]);
            setFilters(prev => ({
              ...prev,
              collaboratorType: type === "researcher" ? "researcher" : "institution"
            }));
          }}
          selectedEntities={selectedEntities}
          onEntitiesChange={(entities) => {
            setTableLoading(true);
            setSelectedEntities(entities);
            setSearchOptions([]);

            if (entities.length > 0) {
              const type = entities[0].type;
              if (type && type !== entityType) {
                setEntityType(type);
              }
              setFilters(prev => ({
                ...prev,
                collaboratorType: type === "researcher" ? "researcher" : "institution"
              }));
            }
          }}
          onEntitiesClear={handleEntityClear}
          onPendingChange={handlePendingChange}
          searchOptions={searchOptions}
          onSearch={handleSearch}
        />

        <div className="flex flex-col lg:flex-row items-start flex-1 gap-6 w-full h-full">
          {/* Always show FilterRow */}
          <div className="w-full lg:w-[280px] flex-shrink-0 lg:sticky lg:top-0 h-max max-h-screen overflow-y-auto bg-white border border-gray-300 rounded-lg">
            <FiltersRow
              filters={filters}
              onFilterChange={setFilters}
              entityType={selectedEntities[0]?.type || "institution"}
              entityHasPending={pendingEntities.hasChanges}
              onRunAnalysis={(newFilters) => {
                setTableLoading(true);
                // Apply pending entities first
                if (pendingEntities.hasChanges) {
                  const entities = pendingEntities.entities;
                  setSelectedEntities(entities);
                  if (entities.length > 0) {
                    const type = entities[0].type;
                    if (type && type !== entityType) setEntityType(type);
                    setFilters(prev => ({ ...prev, ...newFilters, collaboratorType: type === "researcher" ? "researcher" : "institution" }));
                  } else {
                    setFilters(newFilters);
                    handleEntityClear();
                  }
                } else {
                  setFilters(newFilters);
                }
              }}
            />
          </div>

          {/* Right side content */}
          <div className="flex-1 min-w-0 flex flex-col bg-white border border-gray-300 rounded-lg h-full w-full overflow-hidden shadow-sm">
            <Tabs value={viewMode} onValueChange={setViewMode} className="flex-1 flex flex-col min-w-0 h-full">
              {/* View Mode Switcher and Actions */}
              <div className="flex justify-between items-center px-6 py-3 border-b border-gray-100 bg-white">
                <TabsList className="flex h-auto bg-slate-100/80 p-1 rounded-full border border-slate-200">
                  <TabsTrigger
                    value="table"
                    className="flex items-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-explore data-[state=active]:shadow-sm text-slate-500 hover:text-slate-900"
                  >
                    <Table size={16} />
                    <span>Table View</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="visual"
                    className="flex items-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-explore data-[state=active]:shadow-sm text-slate-500 hover:text-slate-900"
                  >
                    <BarChart2 size={16} />
                    <span>Visual View</span>
                  </TabsTrigger>
                </TabsList>

                <div className="flex items-center gap-2 pb-2 mt-2 md:mt-0">
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="sm" onClick={handleExportXLSX} className="flex items-center gap-2 text-gray-700">
                          <Table size={16} /> Export XLSX
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Export data to XLSX</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="sm" onClick={handleAddToReport} className="flex items-center gap-2 text-gray-700">
                          <FilePlus size={16} /> Add to Report
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Add this view to a report</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-auto bg-white p-6">
                <TabsContent forceMount={true} value="table" className="h-full m-0 data-[state=inactive]:hidden">
                  <AnalysisTable
                    filters={filters}
                    entityType={entityType}
                    selectedEntities={selectedEntities}
                    onPinnedChange={setPinnedEntities}
                    onRowsChange={setTableRows}
                    onRawRowsChange={setRawTableRows}
                    onLoadingChange={setTableLoading}
                    onSelectedEntitiesChange={handleSelectedEntitiesChange}
                    sortConfig={sortConfig}
                    onSortChange={setSortConfig}
                  />
                </TabsContent>
                <TabsContent forceMount={true} value="visual" className="h-full m-0 p-0 data-[state=inactive]:hidden">
                  <VisualViewTest
                    pinnedEntities={pinnedEntities}
                    tableRows={tableRows}
                    tableRecords={rawTableRows}
                    isActive={viewMode === "visual"}
                    isTableReady={!tableLoading && tableRows.length > 0}
                    filters={filters}
                    entityType={entityType}
                    initialChartType={searchParams.get("chart_type") || "trend"}
                    initialMetricType={searchParams.get("metric_type") || "total_pubs"}
                    topN={topN}
                    onTopNChange={setTopN}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>

      <AddToReportModal
        open={isAddToReportModalOpen}
        onClose={() => setIsAddToReportModalOpen(false)}
        entity={exportEntities[0]}
        entities={exportEntities}
        entityType={entityType}
        filters={filters}
        exportFormat={exportFormat}
        module="analysis"
        sortBy={ANALYSIS_SORT_MAP[sortConfig.key] || 'total_pubs'}
        sortOrder={sortConfig.direction}
        isPinned={pinnedEntities.length > 0}
        topN={topN}
      />

    </div>
  );
};

export default Analysis;
